import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Radar, FileText, Sparkles, TrendingUp } from 'lucide-react';

const PRICE_TABLE = {
  UK: { sub: '£4.99/mo', radar: '£9.99', report: '£14.99', bundle: '£19.99' },
  IN: { sub: '₹499/mo', radar: '₹999', report: '₹1,499', bundle: '₹1,999' },
  CA: { sub: 'C$8.99/mo', radar: 'C$14.99', report: 'C$19.99', bundle: 'C$27.99' },
};

/**
 * SubscriptionsAndFeesCard — single source of truth for what Shopperden charges.
 * Renders both buyer subscription + seller add-on fees, links to /pulse and
 * each listing's Boost Panel. Lives at the top of buyer/seller dashboards.
 */
const SubscriptionsAndFeesCard = ({ country = 'UK', role = 'seller', dataTestId = 'subscriptions-fees-card' }) => {
  const cc = (country || 'UK').toUpperCase();
  const p = PRICE_TABLE[cc] || PRICE_TABLE.UK;
  const isSeller = role === 'seller' || role === 'landlord';

  return (
    <div data-testid={dataTestId} className="luxury-card p-5 sm:p-6 mb-6 bg-gradient-to-br from-[#1F3D2B] via-[#2A4F39] to-[#1F3D2B] text-white">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <span className="text-[10px] tracking-[0.4em] text-[#E07A3A] uppercase font-semibold">Plans & Fees</span>
          <h3 className="font-['Playfair_Display'] text-2xl mt-1 leading-tight">
            AI Network — matchmaking & data fees
          </h3>
          <p className="text-sm text-white/70 mt-1">
            One Pulse Engine. No agency. Pay for matchmaking &amp; market intelligence only.
          </p>
        </div>
        <Link to="/pulse">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white text-xs tracking-widest font-semibold uppercase transition-colors" data-testid="pulse-marketing-link">
            <Activity className="h-4 w-4" />
            Pulse Premium ({p.sub})
          </span>
        </Link>
      </div>

      {isSeller ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-4 rounded">
            <Radar className="h-5 w-5 text-[#E07A3A] mb-2" />
            <h4 className="font-medium text-sm">Developments Radar</h4>
            <p className="text-xs text-white/60 mt-1">Planning + new builds within 1km</p>
            <p className="text-lg font-['Playfair_Display'] mt-2">{p.radar}</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-4 rounded">
            <FileText className="h-5 w-5 text-[#E07A3A] mb-2" />
            <h4 className="font-medium text-sm">Pulse Boost Report</h4>
            <p className="text-xs text-white/60 mt-1">AI report — raise your Pulse</p>
            <p className="text-lg font-['Playfair_Display'] mt-2">{p.report}</p>
          </div>
          <div className="bg-gradient-to-br from-[#E07A3A]/20 to-transparent border border-[#E07A3A]/40 p-4 rounded">
            <Sparkles className="h-5 w-5 text-[#E07A3A] mb-2" />
            <h4 className="font-medium text-sm">Boost Bundle</h4>
            <p className="text-xs text-white/60 mt-1">Both add-ons, save vs single</p>
            <p className="text-lg font-['Playfair_Display'] mt-2">{p.bundle}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-4 rounded">
            <Activity className="h-5 w-5 text-[#E07A3A] mb-2" />
            <h4 className="font-medium text-sm">Pulse Premium · Email-Only</h4>
            <p className="text-xs text-white/60 mt-1">Top-10 GREEN listings · weekly digest</p>
            <p className="text-lg font-['Playfair_Display'] mt-2">{p.sub}</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-4 rounded">
            <TrendingUp className="h-5 w-5 text-[#E07A3A] mb-2" />
            <h4 className="font-medium text-sm">Free forever</h4>
            <p className="text-xs text-white/60 mt-1">Pulse badge on every listing</p>
            <p className="text-lg font-['Playfair_Display'] mt-2">£0</p>
          </div>
        </div>
      )}

      <p className="text-[11px] text-white/40 mt-4">
        {isSeller
          ? 'Matchmaking Service Fees are one-time, per listing. No agency relationship — pure data & intelligence products.'
          : 'Monthly Pulse Premium — Matchmaking Service Fee via Stripe. Cancel any time. One-click unsubscribe.'}
      </p>
    </div>
  );
};

export default SubscriptionsAndFeesCard;
